import { NewsEndpoint as NewsEndpointInterface } from '@apiClient/interfaces/endpoints/News.endpoint';
import ApiResponseDto from '@apiClient/interfaces/response/ApiResponse.dto';
import { buildSearchParams } from '@/utils/handlers/urlHandler';
import { KyInstance } from "ky/distribution/types/ky";
import { KyResponse } from 'ky';
import NewsDto, { NewsCategoryDto } from '@apiClient/interfaces/models/news/News.dto';
import NewsSearchDto from '@/projectRoot/api-client/interfaces/request/news/NewsSearch.dto';
import PaginationDto from '@/projectRoot/api-client/interfaces/request/Pagination.dto';
import NewsCategoriesSearchDto from '@/projectRoot/api-client/interfaces/request/news/NewsCategoriesSearch.dto';

export class NewsEndpoint implements NewsEndpointInterface {

    private _httpClient: KyInstance;
    private cache: RequestCache;

    public constructor(client: KyInstance, cache: RequestCache = "force-cache") {
        this._httpClient = client;
        this.cache = cache;
    }

    get httpClient(): KyInstance {
        if (!this._httpClient) {
            throw new Error('Ky http client is not initialized');
        }
        return this._httpClient;
    }

    set httpClient(kyInstance: KyInstance) {
        this._httpClient = kyInstance;
    }

    /**
    * /api/news
    */
    async search(params?: NewsSearchDto): Promise<ApiResponseDto<NewsDto[], PaginationDto>> {
        return this._httpClient
            .get(`news`, { searchParams: buildSearchParams(params), cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<NewsDto[], PaginationDto> = await kyResponse.json();
                return response;
            });
    }

    /**
    * /api/news/{id}
    */
    async single(id: string): Promise<ApiResponseDto<NewsDto, undefined>> {
        return this._httpClient
            .get(`news/${id}`)
            .then((response: KyResponse) => {
                if (response.status < 200 || response.status > 299) {
                    throw new Error(response.statusText)
                }
                return response.json();
            });
    }

    /**
    * /api/news/by-url-slug
    */
    async singleByUrlSlug(urlSlug: string, lang?: string): Promise<ApiResponseDto<NewsDto, undefined>> {
        // console.log('urlSlug: ', urlSlug)
        return this._httpClient
            .get(`news/by-url-slug`, { searchParams: buildSearchParams({ url_slug: urlSlug }) })
            .then((response: KyResponse) => {
                console.log('RES: ', response);
                if (response.status < 200 || response.status > 299) {
                    throw new Error(response.statusText)
                }
                return response.json();
            }).catch((err) => {
                console.log('err: ', err);
            });
    }

    /**
    * Return found news, always ordered by parent_breadcrumbs and order field
    * /api/news/draft/{id}
    */

    async draft(id: string): Promise<ApiResponseDto<NewsDto, undefined>> {
        return this._httpClient
            .get(`news/draft/${id}`, { cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText);
                }
                const response: ApiResponseDto<NewsDto, undefined> = await kyResponse.json();
                return response;
            });
    }

    async categoriesSearch(params?: NewsCategoriesSearchDto): Promise<ApiResponseDto<NewsCategoryDto[], PaginationDto>> {
        return this._httpClient
            .get(`news/categories`, { searchParams: buildSearchParams(params), cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<NewsCategoryDto[], PaginationDto> = await kyResponse.json();
                return response;
            });
    }

    async categoriesSingle(id: string): Promise<ApiResponseDto<NewsCategoryDto, undefined>> {
        return this._httpClient
            .get(`news/categories/${id}`)
            .then((response: KyResponse) => {
                if (response.status < 200 || response.status > 299) {
                    throw new Error(response.statusText)
                }
                return response.json();
            });
    }
}

export default NewsEndpoint;